/**
 * Mixin for displaying automated message summary information consistently across components
 * Provides helper methods for formatting and computing display properties
 */
export default {
    methods: {
        /**
         * Format the send_from field for display
         * @param {Object} template - The automated message template
         * @returns {string} Formatted send from text
         * */
        autoMessagePrettySendFrom(template) {
            if (template.conditions?.length) {
                const conditions_with_send_from = template.conditions.filter(({send_from}) => send_from != null);
                
                if (conditions_with_send_from.length !== template.conditions.length) {
                    return 'Not set for some conditions';
                }

                const unique_send_from_values = new Set(conditions_with_send_from.map(({send_from}) => send_from));
                
                if (unique_send_from_values.size === 1) {
                    const sendFrom = conditions_with_send_from[0].send_from;
                    const tn = this.$rc.pretty(sendFrom, 'endpoint');
                    
                    if (template.send_from_label == null) {
                        return tn;
                    }
                    
                    return `${template.send_from_label} - ${tn}`;
                }
                
                return 'Multiple (depends on conditions)';
            }

            if (template.send_from == null) {
                return 'Not Set';
            }

            const tn = this.$rc.pretty(template.send_from, 'endpoint');

            if (template.send_from_label == null) {
                return tn;
            }

            return `${template.send_from_label} - ${tn}`;
        },

        /**
         * Get the chip color for a send mode
         * @param {string} mode - The send mode (auto, manual, disabled)
         * @returns {string} Vuetify color name
         */
        autoMessageGetModeChipColor(mode) {
            switch(mode) {
                case 'auto':
                    return 'success';
                case 'manual':
                    return 'warning';
                case 'disabled':
                    return 'error';
                default:
                    return 'grey';
            }
        },

        /**
         * Get the icon for a send mode
         * @param {string} mode - The send mode (auto, manual, disabled)
         * @returns {string} Material Design icon name
         */
        autoMessageGetModeIcon(mode) {
            switch(mode) {
                case 'auto':
                    return 'mdi-play-circle';
                case 'manual':
                    return 'mdi-account-check';
                case 'disabled':
                    return 'mdi-stop-circle';
                default:
                    return 'mdi-help-circle';
            }
        },

        /**
         * Check if a template has any issues/notes
         * @param {Object} template - The automated message template
         * @returns {boolean} True if there are issues
         */
        autoMessageHasIssues(template) {
            return this.autoMessageGetAllNotes(template).length > 0;
        },

        /**
         * Get all notes/warnings for a template
         * @param {Object} template - The automated message template
         * @returns {Array<string>} Array of note messages
         */
        autoMessageGetAllNotes(template) {
            const notes = [];

            if (template.comp_best_send_mode === 'disabled') {
                notes.push("This template is disabled and will not send any messages. Change the send mode to 'Auto' or 'Manual' in the template settings.");
            }

            if (template.send_from == null) {
                notes.push("No messages can be sent because no 'Send From' number has been selected. Configure a send from number in the template settings.");
            }

            if (['birthday-message', 'birthday-month-message'].includes(template.template_context)) {
                if (template.data_cleaning_certified === false) {
                    notes.push("Birthday messages cannot be sent until you certify your data cleaning rules.");
                }
            }

            if (template.template_context === 'order-placed-request-order-confirmation') {
                if (!template.order_confirmation_settings_id) {
                    notes.push("No messages can be sent because no Order Confirmation form is set. Select an Order Confirmation form to use in the settings.");
                }
            }

            if (template.comp_best_send_mode === 'manual') {
                notes.push("This message will only be capable to send once you manually review and approve each message. All messages will be queued for your review.");
            }

            return notes;
        },

        /**
         * Get order confirmation form linkage status for a template
         * @param {Object} template - The automated message template
         * @param {string} [context_order_conf_settings_id] - Optional current order conf settings ID for context
         * @returns {Object|null} Object with status and label, or null if not applicable
         */
        autoMessageGetOrderConfLinkageStatus(template, context_order_conf_settings_id = null) {
            if (template.template_context !== 'order-placed-request-order-confirmation') {
                return null;
            }

            if (!template.order_confirmation_settings_id) {
                return {
                    status: 'none',
                    label: 'No form set',
                    color: 'error',
                    icon: 'mdi-link-off',
                };
            }

            if (context_order_conf_settings_id && template.order_confirmation_settings_id === context_order_conf_settings_id) {
                return {
                    status: 'current',
                    label: 'Using this form',
                    color: 'success',
                    icon: 'mdi-check-circle',
                };
            }

            if (context_order_conf_settings_id && template.order_confirmation_settings_id !== context_order_conf_settings_id) {
                return {
                    status: 'different',
                    label: 'Using a different form',
                    color: 'warning',
                    icon: 'mdi-alert',
                };
            }

            return null;
        },
    },
};
